/**
 * Stripe Payment Service
 *
 * Comprehensive payment processing service using Stripe
 * Supports:
 * - In-person card payments (Payment Intents)
 * - Mobile payment links (Checkout Sessions)
 * - Payment Request API (Apple Pay, Google Pay)
 */

import { loadStripe, Stripe, StripeElements, PaymentIntent } from '@stripe/stripe-js';

// Load Stripe publishable key from environment
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripePublishableKey) {
  console.error('⚠️ REACT_APP_STRIPE_PUBLISHABLE_KEY is missing from environment variables');
}

// Firebase Functions URL for payment API — derive from the ACTIVE project id (same pattern as
// terminalService) so a STAGING build hits the STAGING functions (test-mode Stripe, or a clean 404),
// NEVER prod. The old hardcoded prod URL meant createPaymentIntent / createCheckoutSession / createRefund
// fired against PROD Stripe from staging = real charges/refunds while testing. (Jose 2026-10-29 money audit)
const PAY_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy';
const FUNCTIONS_URL = `https://us-central1-${PAY_PROJECT_ID}.cloudfunctions.net`;

// Initialize Stripe
let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise && stripePublishableKey) {
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise || Promise.resolve(null);
};

// Types
export interface PaymentIntentRequest {
  amount: number; // in dollars
  currency?: string;
  description?: string;
  metadata?: Record<string, string>;
  customerEmail?: string;
  customerPhone?: string;
}

export interface PaymentLinkRequest {
  items: Array<{
    name: string;
    price: number; // in dollars
    quantity: number;
    description?: string;
  }>;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  metadata?: Record<string, string>;
}

export interface PaymentResult {
  success: boolean;
  paymentIntent?: PaymentIntent;
  error?: string;
  paymentMethodId?: string;
}

/**
 * Create a Payment Intent for in-person card payments
 * This is used when the customer is present and entering their card
 */
export const createPaymentIntent = async (
  request: PaymentIntentRequest
): Promise<{ clientSecret: string; paymentIntentId: string }> => {
  try {
    // In production, this should call your backend API
    // For now, we'll simulate the response structure

    // Call Firebase Functions for payment intent
    const response = await fetch(`${FUNCTIONS_URL}/createPaymentIntent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: Math.round(request.amount * 100), // Convert to cents
        currency: request.currency || 'usd',
        description: request.description,
        metadata: request.metadata,
        receipt_email: request.customerEmail,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create payment intent');
    }

    const data = await response.json();
    return {
      clientSecret: data.clientSecret,
      paymentIntentId: data.paymentIntentId,
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error('Failed to create payment intent. Please try again.');
  }
};

/**
 * CARD ON FILE (Jose 2026-09-06) — charge a client's SAVED card off-session at
 * checkout. Hits the same createPaymentIntent function but passes customerId +
 * paymentMethodId, so the server confirms with off_session:true and returns the
 * PaymentIntent status. A real, refundable charge exists ONLY when the returned
 * status === 'succeeded'. Any other status (e.g. requires_action / 3-DS) OR an
 * HTTP error means NOT charged — the caller MUST NOT record the sale as paid.
 * Result shape mirrors terminalService.collectCardPayment so the checkout money
 * path stays uniform.
 */
export const chargeCardOnFile = async (params: {
  amount: number; // dollars
  customerId: string; // Stripe customer id (client.stripeCustomerId)
  paymentMethodId: string; // Stripe payment method id (savedPaymentMethod.stripePaymentMethodId)
  description?: string;
  metadata?: Record<string, string>;
  receiptEmail?: string;
}): Promise<{ success: boolean; paymentIntentId?: string; status?: string; error?: string }> => {
  try {
    const response = await fetch(`${FUNCTIONS_URL}/createPaymentIntent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: Math.round(params.amount * 100), // cents
        currency: 'usd',
        description: params.description,
        metadata: params.metadata,
        receipt_email: params.receiptEmail,
        customerId: params.customerId,
        paymentMethodId: params.paymentMethodId,
      }),
    });
    const data = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      // Stripe throws authentication_required / card_declined here → server 500s with the message.
      return { success: false, error: data?.error || 'Card on file could not be charged.' };
    }
    if (data.status !== 'succeeded') {
      return {
        success: false,
        paymentIntentId: data.paymentIntentId,
        status: data.status,
        error: `Card on file needs further action (${data.status || 'unknown'}) — charge not completed. Try the reader or another tender.`,
      };
    }
    return { success: true, paymentIntentId: data.paymentIntentId, status: data.status };
  } catch (error: any) {
    console.error('Error charging card on file:', error);
    return { success: false, error: error?.message || 'Card on file charge failed. Please try again.' };
  }
};

/**
 * Confirm a card payment using Stripe Elements
 * This processes the actual payment
 */
export const confirmCardPayment = async (
  clientSecret: string,
  elements: StripeElements,
  customerName: string
): Promise<PaymentResult> => {
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/payment-success',
        payment_method_data: {
          billing_details: {
            name: customerName,
          },
        },
      },
      redirect: 'if_required', // Don't redirect for POS payments
    });

    if (error) {
      return {
        success: false,
        error: error.message,
      };
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      return {
        success: true,
        paymentIntent,
        paymentMethodId: paymentIntent.payment_method as string,
      };
    }

    return {
      success: false,
      error: 'Payment was not successful',
    };
  } catch (error: any) {
    console.error('Error confirming payment:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
};

/**
 * Create a payment link for mobile/remote payments
 * This generates a Stripe Checkout Session that can be sent via SMS/email
 */
export const createPaymentLink = async (
  request: PaymentLinkRequest
): Promise<{ checkoutUrl: string; sessionId: string }> => {
  try {
    // Call Firebase Functions for checkout session
    const response = await fetch(`${FUNCTIONS_URL}/createCheckoutSession`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lineItems: request.items.map(item => ({
          name: item.name,
          description: item.description,
          amount: Math.round(item.price * 100), // Convert to cents
          quantity: item.quantity,
        })),
        customerName: request.customerName,
        customerEmail: request.customerEmail,
        customerPhone: request.customerPhone,
        metadata: request.metadata,
        successUrl: window.location.origin + '/payment-success?session_id={CHECKOUT_SESSION_ID}',
        cancelUrl: window.location.origin + '/payment-cancelled',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const data = await response.json();
    return {
      checkoutUrl: data.url,
      sessionId: data.sessionId,
    };
  } catch (error) {
    console.error('Error creating payment link:', error);
    throw new Error('Failed to create payment link. Please try again.');
  }
};

/**
 * Check if Apple Pay or Google Pay is available
 * Used for the "Mobile Pay" button
 */
export const checkPaymentRequestAvailability = async (): Promise<boolean> => {
  try {
    const stripe = await getStripe();
    if (!stripe) return false;

    const paymentRequest = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Test',
        amount: 100,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    const canMakePayment = await paymentRequest.canMakePayment();
    return !!canMakePayment;
  } catch (error) {
    console.error('Error checking payment request availability:', error);
    return false;
  }
};

/**
 * Create a Payment Request for Apple Pay / Google Pay
 */
export const createPaymentRequest = async (
  amount: number,
  label: string
): Promise<any> => {
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    const paymentRequest = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: label,
        amount: Math.round(amount * 100), // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    // Check if payment method is available
    const canMakePayment = await paymentRequest.canMakePayment();
    if (!canMakePayment) {
      throw new Error('Apple Pay or Google Pay is not available on this device');
    }

    return paymentRequest;
  } catch (error: any) {
    console.error('Error creating payment request:', error);
    throw new Error(error.message || 'Failed to initialize mobile payment');
  }
};

/**
 * Retrieve payment intent status
 * Used to check if a payment has been completed
 */
export const retrievePaymentIntent = async (
  paymentIntentId: string
): Promise<PaymentIntent | null> => {
  try {
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    const { paymentIntent } = await stripe.retrievePaymentIntent(paymentIntentId);
    return paymentIntent || null;
  } catch (error) {
    console.error('Error retrieving payment intent:', error);
    return null;
  }
};

/**
 * Refund a sale to the customer's card (Jose 2026-08-21). Calls the createRefund Cloud Function,
 * which refunds the sale's Stripe PaymentIntent and writes refundAmount/status back. Only works for
 * sales actually charged through Stripe (payment links) — the UI voids external/cash sales locally.
 */
export const refundSale = async (
  appointmentId: string,
  amount?: number
): Promise<{ success: boolean; amount?: number; error?: string; code?: string }> => {
  try {
    const response = await fetch(`${FUNCTIONS_URL}/createRefund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appointmentId, amount }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.message || data.error || 'Refund failed', code: data.error };
    }
    return { success: true, amount: data.amount };
  } catch (e: any) {
    return { success: false, error: e?.message || 'Refund failed' };
  }
};

// Export Stripe types for use in components
export type { Stripe, StripeElements };
